import { useEffect, useRef, useState } from 'react';
import { twMerge } from 'tailwind-merge';
import { TFunction } from 'i18next';
import { KpiObjectType } from '@/api/entities/kpi';
import { IouDistributionBucket } from '@/type/kpi-dashboard.types';
import {
  buildAxisTicks,
  getAxisLabelStep,
  getKpiObjectLabel,
  getNiceAxisMax,
  KPI_TIER_COLORS,
} from '@/utils/kpi-dashboard.utils';

interface IouDistributionChartProps {
  t?: TFunction<undefined>;
  type: KpiObjectType;
  buckets: IouDistributionBucket[];
  containerClassName?: string;
}

/** Height reserved under the plot area for the x axis labels. */
const X_AXIS_HEIGHT = 24;
const getBucketLabelStyle = (
  index: number,
  bucketCount: number,
): React.CSSProperties => {
  if (index === 0) return { left: 0 };
  if (index === bucketCount - 1) return { right: 0 };

  return {
    left: `${((index + 0.5) / bucketCount) * 100}%`,
    transform: 'translateX(-50%)',
  };
};

export const IouDistributionChart = ({
  t,
  type,
  buckets,
  containerClassName,
}: IouDistributionChartProps) => {
  const maxCount = Math.max(...(buckets ?? []).map(({ count }) => count ?? 0));
  const axisMax = getNiceAxisMax(maxCount);
  const ticks = buildAxisTicks(axisMax);

  const plotRef = useRef<HTMLDivElement>(null);
  const [plotWidth, setPlotWidth] = useState(0);

  useEffect(() => {
    const plot = plotRef.current;

    if (!plot || typeof ResizeObserver === 'undefined') return undefined;

    const observer = new ResizeObserver(([entry]) =>
      setPlotWidth(entry?.contentRect?.width ?? 0),
    );

    observer.observe(plot);

    return () => observer.disconnect();
  }, []);

  const bucketCount = (buckets ?? []).length;
  const labelStep = getAxisLabelStep(bucketCount, plotWidth);

  return (
    <div className={twMerge('h-64 w-full', containerClassName)}>
      <div className="flex h-full w-full">
        <div
          className="relative w-8 shrink-0 pr-2"
          style={{ marginBottom: X_AXIS_HEIGHT }}
        >
          {ticks.map((tick, index) => (
            <span
              key={tick}
              className="absolute right-2 translate-y-1/2 text-2xs leading-none text-gray-78829D tabular-nums"
              style={{ bottom: `${(index / (ticks.length - 1)) * 100}%` }}
            >
              {tick}
            </span>
          ))}
        </div>

        <div className="relative min-w-0 flex-1" ref={plotRef}>
          <div
            className="absolute inset-x-0 top-0"
            style={{ bottom: X_AXIS_HEIGHT }}
          >
            {ticks.map((tick, index) => (
              <div
                key={tick}
                className="absolute inset-x-0 h-px bg-gray-DBDFE9"
                style={{ bottom: `${(index / (ticks.length - 1)) * 100}%` }}
              />
            ))}
          </div>

          <div
            className="absolute inset-x-0 top-0 flex items-end"
            style={{ bottom: X_AXIS_HEIGHT }}
          >
            {(buckets ?? []).map((bucket) => (
              <div
                key={bucket?.label}
                className="group flex h-full flex-1 items-end justify-center"
              >
                <div
                  className="relative w-[80%]"
                  style={{
                    height: `${((bucket?.count ?? 0) / axisMax) * 100}%`,
                  }}
                >
                  <div
                    className="h-full w-full rounded-t-[6px] transition-opacity group-hover:opacity-80"
                    style={{
                      backgroundColor: KPI_TIER_COLORS[bucket?.tier]?.solid,
                    }}
                  />
                  <div className="pointer-events-none absolute bottom-full left-1/2 z-10 mb-2 hidden -translate-x-1/2 whitespace-nowrap rounded-lg border border-gray-DBDFE9 bg-white px-2.5 py-2 text-2xs shadow-default group-hover:block">
                    <p className="font-medium text-black-071336">
                      {bucket?.label}
                    </p>
                    <p className="text-gray-78829D">
                      {t('objectsCount', {
                        value: bucket?.count ?? 0,
                        objects: getKpiObjectLabel(t, type, bucket?.count ?? 0),
                      })}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div
            className="absolute inset-x-0 bottom-0"
            style={{ height: X_AXIS_HEIGHT }}
          >
            {(buckets ?? []).map((bucket, index) => {
              if (index % labelStep !== 0) return null;

              // The outer labels align to the plot edges rather than to their
              // bucket centre, so half of them cannot hang off the card.
              const style = getBucketLabelStyle(index, bucketCount);

              return (
                <span
                  key={bucket?.label}
                  className="absolute top-1.5 whitespace-nowrap text-2xs leading-none text-gray-78829D"
                  style={style}
                >
                  {bucket?.label}
                </span>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};
